// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as addressApi from '../api'

//Tiempo de vida del catálogo de address en el store (30 minutos)
export const ADDRESS_CACHE_TTL = 30 * 60 * 1000

const asList = (data) => (Array.isArray(data) ? data : [])
const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getAddress = createAsyncThunk('appAddress/getAddress', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await addressApi.fetchAddresses(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const address = getState()?.address

        if (!address) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (address.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (address.status === 'pending') return false
        //Store vacío
        if (!address.data?.length) return true

        return Date.now() - (address.lastFetch ?? 0) > ADDRESS_CACHE_TTL
    }
})

export const appAddressSlice = createSlice({
    name: 'appAddress',
    initialState: {
        data: [],
        isRequested: true,
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getAddress.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getAddress.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
        })
        builder.addCase(getAddress.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = action.payload ?? action.error?.message ?? null
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { enableRequest } = appAddressSlice.actions

//La recarga no se espera: el alta/edición se resuelve en cuanto responde el backend y
//la lista nueva llega después con `status: 'pending'` mientras tanto
export const addAddress = createAsyncThunk('appAddress/addAddress', async (address, { dispatch, rejectWithValue }) => {
    try {
        const data = await addressApi.createAddress(address)
        dispatch(enableRequest())
        dispatch(getAddress())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateAddress = createAsyncThunk('appAddress/updateAddress', async (payload, { dispatch, rejectWithValue }) => {
    try {
        const data = await addressApi.updateAddress(payload.id, payload.address)
        dispatch(enableRequest())
        dispatch(getAddress())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appAddressSlice.reducer
